import { ToastService } from "../../../services/toast.ts";
import { OrganizationInvitesService, OrganizationMember, OrganizationInvite } from "../../../services/organization-invites.ts";
import { OrganizationService, Organization } from "../../../services/organization.ts";
import { authService } from '../../../services/auth.ts';
import { SubNav } from '../../../components/library/ui/sub-nav.ts';
import { AppSaveBar } from '../../../components/library/ui/app-save-bar.ts';
import '../../../components/library/ui/app-card.ts';
import '../../../components/library/ui/app-button.ts';
import '../../../components/library/ui/app-badge.ts';
import '../../../components/library/ui/app-icon.ts';
import '../../../components/library/ui/app-modal.ts';

import './tabs/general.ts';
import './tabs/users.ts';
import './tabs/audit.ts';
import './tabs/billing.ts';
import './tabs/tags.ts';
import './tabs/danger.ts';
import './tabs/api-keys.ts';
import '../../../components/payment-form.ts';
import { SecretService } from '../../../services/secret.ts';

export class AppOrganization extends HTMLElement {
  private _orgService = OrganizationService.getInstance();
  private _inviteService = OrganizationInvitesService.getInstance();
  private _toastService = ToastService.getInstance();
  
  private _orgId: string = '';
  private _org: Organization | null = null;
  private _members: OrganizationMember[] = [];
  private _invites: OrganizationInvite[] = [];
  private _auditLogs: any[] = [];
  private _auditLogOffset: number = 0;
  private _auditLogTotal: number = 0;
  private _auditLogFilters: any = {};
  private _tags: any[] = [];
  private _forbidden: boolean = false;
  private _error: string | null = null;
  private _isAdmin: boolean = false;
  private _subscription: any = null;
  private _invoices: any[] = [];
  private _usage: number = 0;
  private _mauUsage: number = 0;
  private _paymentMethod: any = null;
  private _availableModules: any[] = [];
  private _plans: any[] = [];
  private _currentUser: any = null;
  
  private _isLoading: boolean = false;
  private _isFetching: boolean = false;
  private _lastFetchId: number = 0;
  private _currentTab: 'general' | 'users' | 'audit' | 'billing' | 'tags' | 'danger' | 'api-keys' = 'general';
  private _previewDebounceTimer: any = null;
  private _isPaymentModalOpen: boolean = false;
  private _listenersAttached: boolean = false;

  constructor() {
    super();
  }

  static get observedAttributes() {
    return ['org-id', 'tab'];
  }

  async attributeChangedCallback(name: string, oldVal: string, newVal: string) {
    if (oldVal === newVal) return;
    
    if (name === 'org-id') {
      console.log('[AppOrganization] org-id changed:', newVal);
      this._orgId = newVal;
      this._org = null; // Clear org to force reload
    }
    if (name === 'tab') {
      this._currentTab = newVal as any;
    }

    if (this.isConnected && !this._isFetching) {
      this.loadData();
      this.emitPanel();
    }
  }

  private resetData() {
    this._org = null;
    this._members = [];
    this._invites = [];
    this._auditLogs = [];
    this._auditLogOffset = 0;
    this._auditLogTotal = 0;
    this._subscription = null;
    this._invoices = [];
    this._usage = 0;
    this._paymentMethod = null;
    this._availableModules = [];
    this._plans = [];
    this._tags = [];
    this._forbidden = false;
    this._error = null;
  }

  async connectedCallback() {
    try {
      const user = authService.getUser();
      this._orgId = user?.org_id || localStorage.getItem('crate_org_id') || '';
      
      const initialTab = this.getAttribute('tab') as any;
      if (initialTab && ['general', 'users', 'audit', 'billing', 'tags', 'danger', 'api-keys'].includes(initialTab)) {
        this._currentTab = initialTab;
      }

      this._currentUser = authService.getUser();
      this.initEvents();
      this.render();
      await this.loadData();
    } catch (e) {
      console.error('[AppOrganization] Initialization failed:', e);
      this._isLoading = false;
      this.render();
    }
    
    requestAnimationFrame(() => this.emitPanel());
  }

  private async loadData() {
    if (!this._orgId) return;
    
    const fetchId = ++this._lastFetchId;
    this._isFetching = true;
    this._isLoading = true;
    this._error = null;
    this._forbidden = false;
    this.render();
    
    console.debug(`[AppOrganization] Loading data (ID: ${fetchId}) for ${this._orgId} (Tab: ${this._currentTab})`);
    
    try {
      if (this._org && this._org.id !== this._orgId) {
        this.resetData();
      }

      if (!this._org) {
        this._org = await this._orgService.get(this._orgId);
        if (fetchId !== this._lastFetchId) return;
      }

      switch (this._currentTab) {
        case 'users':
          const [members, invites] = await Promise.all([
            this._inviteService.getMembers(this._orgId),
            this._inviteService.getInvites(this._orgId)
          ]);
          if (fetchId !== this._lastFetchId) return;
          this._members = members;
          this._invites = invites;
          break;
        case 'audit':
          // Format dates to ISO strings if they are present in filters
          const activeFilters = { ...this._auditLogFilters };
          if (activeFilters.from) activeFilters.from = new Date(activeFilters.from).toISOString();
          if (activeFilters.to) activeFilters.to = new Date(activeFilters.to).toISOString();

          const logsRes = await this._orgService.getAuditLogs(this._orgId, 20, this._auditLogOffset, activeFilters);
          if (fetchId !== this._lastFetchId) return;
          this._auditLogs = logsRes.data || [];
          this._auditLogTotal = logsRes.meta?.total || 0;
          break;
        case 'billing':
          const [sub, invs, usage, mauUsage, pm, am, plans] = await Promise.all([
            this._orgService.getSubscription(this._orgId),
            this._orgService.getInvoices(this._orgId),
            this._orgService.getDataUsage(this._orgId),
            this._orgService.getMauUsage(this._orgId),
            this._orgService.getPaymentMethod(this._orgId),
            this._orgService.getAvailableModules(this._orgId),
            this._orgService.getPlans()
          ]);
          if (fetchId !== this._lastFetchId) return;
          this._subscription = sub;
          this._invoices = invs;
          this._usage = usage || 0;
          this._mauUsage = mauUsage || 0;
          this._paymentMethod = pm;
          this._availableModules = am || [];
          this._plans = plans || [];
          break;
        case 'tags':
          this._tags = await SecretService.getInstance().listTags(this._orgId);
          if (fetchId !== this._lastFetchId) return;
          break;
      }
    } catch (e: any) {
      if (fetchId !== this._lastFetchId) return;
      console.error('[AppOrganization] Error loading data:', e);
      if (e.status === 403) {
        this._forbidden = true;
        this._error = "Forbidden: You do not have access to this organization's settings.";
        try {
            const profile = await authService.getProfile();
            const org = profile?.organizations?.find((o: any) => o.id === this._orgId);
            this._isAdmin = org && (org.role === 'admin' || org.role === 'owner');
        } catch (err) {}
      } else {
        this._error = e.message || "Failed to load organization data.";
      }
    } finally {
      if (fetchId === this._lastFetchId) {
        this._isLoading = false;
        this._isFetching = false;
        this.render();
        this.emitPanel();
      }
    }
  }

  private emitPanel() {
    const tabs = [
      { id: 'general', name: 'General', icon: 'settings' },
      { id: 'users', name: 'Members', icon: 'users' },
      { id: 'tags', name: 'Tags', icon: 'tag' },
      { id: 'api-keys', name: 'API Keys', icon: 'key' },
      { id: 'audit', name: 'Audit Logs', icon: 'activity' },
      { id: 'billing', name: 'Billing', icon: 'credit-card' },
      { id: 'danger', name: 'Danger Zone', icon: 'warning', danger: true }
    ];

    const subNav = document.createElement('app-sub-nav') as SubNav;
    subNav.items = tabs;
    subNav.activeId = this._currentTab;

    subNav.addEventListener('nav-change', ((e: CustomEvent) => {
      const tab = e.detail.id;
      window.dispatchEvent(new CustomEvent('navigate', { 
        detail: { view: 'organization', params: { tab } } 
      }));
    }) as any);

    const activeTabObj = tabs.find(t => t.id === this._currentTab);

    this.dispatchEvent(new CustomEvent('shell-panel-changed', {
      bubbles: true,
      composed: true,
      detail: { 
        panel: subNav,
        header: {
          title: this._org?.name || 'Organization', 
          subtitle: activeTabObj ? activeTabObj.name : 'Settings & Members'
        }
      }
    }));
  }

  private _renderTimeout: any = null;

  render() {
    if (this._renderTimeout) return;
    this._renderTimeout = setTimeout(() => {
        this._renderInternal();
        this._renderTimeout = null;
    }, 0);
  }

  private _renderInternal() {
    if (!this.querySelector('.main-org-container')) {
        this.innerHTML = `
            <style>
              :host { display: block; }
              .org-container { padding: 40px; max-width: 1200px; margin: 0 auto; animation: fadeIn 0.3s ease-out; }
              .tab-content { margin-top: 32px; }
              @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            </style>

            <div class="org-container main-org-container"></div>
            <div class="modal-container"></div>
            <app-save-bar id="org-save-bar"></app-save-bar>
        `;
    }

    const mainContainer = this.querySelector('.main-org-container')!;
    const modalContainer = this.querySelector('.modal-container')!;

    mainContainer.innerHTML = this._forbidden ? this.renderForbidden() : this.renderBody();

    if (this._isPaymentModalOpen) {
        modalContainer.innerHTML = `
          <app-modal id="payment-modal" open size="md">
            <div slot="header" style="display: flex; align-items: center; gap: 12px;">
              <span style="color: var(--accent); font-size: 1.5rem;">💳</span>
              Update Payment Method
            </div>
            
            <app-payment-form id="payment-form" org-id="${this._orgId}" open></app-payment-form>
            
            <div slot="actions">
              <app-button variant="ghost" size="sm" id="btn-payment-cancel" icon="x"></app-button>
              <app-button variant="primary" size="sm" id="btn-payment-submit" icon="check"></app-button>
            </div>
          </app-modal>
        `;
    } else {
        modalContainer.innerHTML = '';
    }

    this.setupListeners();
  }

  private renderBody() {
    if (this._isLoading && !this._org) {
      return `
        <div style="padding: 80px; text-align: center; color: var(--text-muted); font-family: var(--font-mono);">
          FETCHING_ORGANIZATION_DATA...
        </div>
      `;
    }

    return this.renderTabContent();
  }

  private renderForbidden() {
    return `
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: calc(100vh - 100px); gap: 24px; padding: 40px; text-align: center;">
        <div class="sketchy-card" style="max-width: 450px; padding: 40px; background: rgba(15, 23, 42, 0.4); border: 1px dashed rgba(255,255,255,0.1);">
          <div style="position: relative; z-index: 2;">
            <app-icon name="lock" size="48" style="color: var(--text-muted); margin-bottom: 20px; opacity: 0.8;"></app-icon>
            <h1 style="font-family: var(--font-main); font-size: 1.5rem; margin-bottom: 12px; color: var(--text-main); font-weight: 500;">Module Locked</h1>
            <p style="font-family: var(--font-main); color: var(--text-dim); line-height: 1.5; margin-bottom: 28px; font-size: 0.95rem;">
              You don't have permission to access these organization settings.
            </p>
            ${this._isAdmin ? `
            <app-button variant="primary" size="sm" onclick="window.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'organization', params: { tab: 'billing' } } }))" style="width: 100%;">
              Update Subscription
            </app-button>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  private renderTabContent() {
    switch (this._currentTab) {
      case 'general':
        return `<org-general-tab id="general-tab" .org="${JSON.stringify(this._org).replace(/"/g, '&quot;')}" .currentUser="${JSON.stringify(this._currentUser).replace(/"/g, '&quot;')}"></org-general-tab>`;
      case 'users':
        return `<org-users-tab id="users-tab" .members="${JSON.stringify(this._members).replace(/"/g, '&quot;')}" .invites="${JSON.stringify(this._invites).replace(/"/g, '&quot;')}" org-id="${this._orgId}"></org-users-tab>`;
      case 'tags':
        return `<org-tags-tab id="tags-tab" .tags="${JSON.stringify(this._tags || []).replace(/"/g, '&quot;')}" org-id="${this._orgId}"></org-tags-tab>`;
      case 'billing':
        return `<org-billing-tab id="billing-tab" org-id="${this._orgId}"></org-billing-tab>`;
      case 'audit':
        return `<org-audit-tab id="audit-tab" .logs="${JSON.stringify(this._auditLogs).replace(/"/g, '&quot;')}" org-id="${this._orgId}"></org-audit-tab>`;
      case 'api-keys':
        return `<org-api-keys-tab id="api-keys-tab" org-id="${this._orgId}"></org-api-keys-tab>`;
      case 'danger':
        return `<org-danger-tab id="danger-tab"></org-danger-tab>`;
      default:
        return 'Select a tab';
    }
  }

  private initEvents() {
    if (this._listenersAttached) return;
    this._listenersAttached = true;

    this.addEventListener('change', (e: any) => {
      const saveBar = this.querySelector('app-save-bar') as AppSaveBar;
      if (saveBar) {
        saveBar.active = true;
        this.updateSaveBarMessage();
      }
    });

    this.addEventListener('save', () => this.handleSave());
    this.addEventListener('cancel', () => {
        const saveBar = this.querySelector('app-save-bar') as AppSaveBar;
        if (saveBar) {
            saveBar.active = false;
            this.render();
        }
    });

    this.addEventListener('invite-member', async (e: any) => {
        try {
          await this._inviteService.inviteUser(this._orgId, e.detail.email, e.detail.role);
          this._toastService.success('Invitation sent');
          await this.loadData();
        } catch (err: any) {
          this._toastService.error(`Invite failed: ${err.message}`);
        }
    });
      
    this.addEventListener('remove-member', async (e: any) => {
        if (!confirm('Are you sure you want to remove this member?')) return;
        try {
          await this._inviteService.removeMember(this._orgId, e.detail.userId);
          this._toastService.success('Member removed');
          await this.loadData();
        } catch (err: any) {
          this._toastService.error(`Removal failed: ${err.message}`);
        }
    });

    this.addEventListener('revoke-invite', async (e: any) => {
        try {
          await this._inviteService.revokeInvite(this._orgId, e.detail.inviteId);
          this._toastService.success('Invitation revoked');
          await this.loadData();
        } catch (err: any) {
          this._toastService.error(`Revoke failed: ${err.message}`);
        }
    });

    this.addEventListener('delete-org', async () => {
        if (!confirm('Are you absolutely sure you want to delete this organization? This action cannot be undone and will immediately destroy all data associated with it.')) return;
        try {
            await this._orgService.delete(this._orgId);
            this._toastService.success('Organization deleted successfully');
            
            // Switch session and navigate
            const profile = await authService.getProfile();
            const otherOrgs = profile?.organizations?.filter((o: any) => o.id !== this._orgId) || [];
            if (otherOrgs.length > 0) {
                await authService.switchOrganization(otherOrgs[0].id);
                window.location.reload();
            } else {
                window.location.href = '/app/overview';
            }
        } catch (err: any) {
            this._toastService.error(`Deletion failed: ${err.message}`);
        }
    });

    this.addEventListener('rotate-secrets-key', async () => {
        if (!confirm('Are you sure you want to rotate the secrets encryption key? This will decrypt and re-encrypt all secrets in your organization.')) return;
        try {
            await this._orgService.rotateKmsKey(this._orgId, 'secrets');
        } catch (err: any) {
            // Error is handled by api client toast typically, but can log here
            console.error('Failed to rotate secrets key:', err);
        }
    });

    this.addEventListener('rotate-agentvault-key', async () => {
        if (!confirm('Are you sure you want to rotate the agent vault encryption key? This will decrypt and re-encrypt all agent vault items.')) return;
        try {
            await this._orgService.rotateKmsKey(this._orgId, 'agent_vault');
        } catch (err: any) {
            console.error('Failed to rotate agent vault key:', err);
        }
    });

    this.addEventListener('rotate-rsa-key', async () => {
        if (!confirm('Are you sure you want to rotate the RSA transport key? This will affect any inflight requests using the old public key.')) return;
        try {
            await this._orgService.rotateKmsKey(this._orgId, 'rsa');
        } catch (err: any) {
            console.error('Failed to rotate RSA key:', err);
        }
    });
  }

  private setupListeners() {
    // Tab setters and specific events
    const generalTab = this.querySelector('#general-tab') as any;
    if (generalTab) {
      generalTab.org = this._org;
      generalTab.orgId = this._orgId;
    }

    const usersTab = this.querySelector('#users-tab') as any;
    if (usersTab) {
      usersTab.members = this._members;
      usersTab.invites = this._invites;
    }

    const tagsTab = this.querySelector('#tags-tab') as any;
    if (tagsTab) {
      tagsTab.tags = this._tags;
      tagsTab.addEventListener('tags-updated', () => this.loadData());
    }

    const apiKeysTab = this.querySelector('#api-keys-tab') as any;
    if (apiKeysTab) {
      apiKeysTab.orgId = this._orgId;
    }
    
    // ... billing tab binding logic (I need to keep the billing logic)
    const auditTab = this.querySelector('#audit-tab') as any;
    if (auditTab) {
      auditTab.auditLogs = this._auditLogs;
      auditTab.total = this._auditLogTotal;
      auditTab.offset = this._auditLogOffset;
      auditTab.filters = this._auditLogFilters;
      
      auditTab.addEventListener('page-change', (e: any) => {
        this._auditLogOffset = e.detail.offset;
        this.loadData();
      });

      auditTab.addEventListener('filter-change', (e: any) => {
        this._auditLogFilters = e.detail.filters;
        this._auditLogOffset = 0;
        this.loadData();
      });

      auditTab.addEventListener('export-audit', async () => {
        try {
          const activeFilters = { ...this._auditLogFilters };
          if (activeFilters.from) activeFilters.from = new Date(activeFilters.from).toISOString();
          if (activeFilters.to) activeFilters.to = new Date(activeFilters.to).toISOString();
          await this._orgService.exportAuditLogs(this._orgId, activeFilters);
        } catch (e: any) {
          console.error('Export failed', e);
          this._toastService.error('Export failed. Please try again.');
        }
      });
    }

    const billingTab = this.querySelector('#billing-tab') as any;
    if (billingTab) {
      billingTab.subscription = this._subscription;
      billingTab.invoices = this._invoices;
      billingTab.usage = this._usage;
      billingTab.mauUsage = this._mauUsage;
      billingTab.paymentMethod = this._paymentMethod;
      billingTab.availableModules = this._availableModules;
      billingTab.plans = this._plans;

      billingTab.addEventListener('update-payment', () => {
        this._isPaymentModalOpen = true;
        this.render();
      });

      const paymentModal = this.querySelector('#payment-modal');
      const paymentForm = this.querySelector('#payment-form') as any;
      const paymentSubmitBtn = this.querySelector('#btn-payment-submit') as any;
      const paymentCancelBtn = this.querySelector('#btn-payment-cancel') as any;

      if (paymentModal && paymentForm) {
        paymentModal.addEventListener('close', () => {
          this._isPaymentModalOpen = false;
          this.render();
        });

        paymentSubmitBtn?.addEventListener('click', async () => {
          try {
            paymentSubmitBtn.loading = true;
            paymentSubmitBtn.disabled = true;
            await paymentForm.submit();
          } finally {
            paymentSubmitBtn.loading = false;
            paymentSubmitBtn.disabled = false;
          }
        });

        paymentCancelBtn?.addEventListener('click', () => {
          this._isPaymentModalOpen = false;
          this.render();
        });

        paymentForm.addEventListener('payment-updated', async () => {
          this._toastService.success('Payment method updated successfully');
          this._isPaymentModalOpen = false;
          this.render();
          await this.loadData();
        });
      }
      
      billingTab.addEventListener('cancel-subscription', async () => {
        if (!confirm('Are you sure you want to cancel your subscription? You will retain access until the end of the current billing cycle.')) return;
        try {
          await this._orgService.cancelSubscription(this._orgId);
          this._toastService.success('Subscription scheduled for cancellation');
          await this.loadData();
        } catch (err: any) {
          this._toastService.error(`Cancellation failed: ${err.message}`);
        }
      });

      billingTab.addEventListener('download-invoice', async (e: any) => {
        try {
          await this._orgService.downloadInvoice(this._orgId, e.detail.invoiceId);
        } catch (err: any) {
          this._toastService.error(`Failed to download invoice: ${err.message}`);
        }
      });
    }
  }

  private async handleSave() {
    const saveBar = this.querySelector('app-save-bar') as AppSaveBar;
    if (saveBar) saveBar.loading = true;

    try {
      if (this._currentTab === 'general') {
        const tab = this.querySelector('#general-tab') as any;
        const data = tab.getFormData();
        await this._orgService.update(this._orgId, data.name);
      } else if (this._currentTab === 'users') {
        const tab = this.querySelector('#users-tab') as any;
        const roleChanges = tab.getRoleChanges();
        console.log(`[AppOrganization] Detected role changes:`, roleChanges);
        if (roleChanges.length > 0) {
          await Promise.all(roleChanges.map((change: any) => 
            this._inviteService.updateMemberRole(this._orgId, change.userId, change.role)
          ));
          this._toastService.success('Roles updated successfully');
        }
      } else if (this._currentTab === 'billing') {
        const tab = this.querySelector('#billing-tab') as any;
        const changes = tab.getChanges();
        
        // 1. Preview
        const preview = await this._orgService.previewSubscriptionUpdate(this._orgId, changes.planId, changes.moduleIds);
        
        let message = 'Are you sure you want to save these subscription changes?';
        const delta = preview.delta_cents || 0;
        if (delta > 0) {
          message = `You will be charged $${(delta / 100).toFixed(2)} immediately for these changes. Confirm?`;
        } else if (delta < 0) {
          message = `These changes will result in a credit of $${(Math.abs(delta) / 100).toFixed(2)} to your account. Confirm?`;
        }

        if (!confirm(message)) {
          if (saveBar) saveBar.loading = false;
          return;
        }

        // 2. Update
        await this._orgService.updateSubscription(this._orgId, changes.planId, changes.moduleIds);
      }
      await this.loadData();
      if (saveBar) saveBar.active = false;
    } catch (e: any) {
      // Toast handled by ApiClient
    } finally {
      if (saveBar) saveBar.loading = false;
    }
  }

  private async updateSaveBarMessage() {
    const saveBar = this.querySelector('app-save-bar') as AppSaveBar;
    if (!saveBar) return;

    if (this._currentTab !== 'billing') {
      saveBar.removeAttribute('message');
      return;
    }

    const tab = this.querySelector('#billing-tab') as any;
    if (!tab || !tab.getChanges) return;

    const changes = tab.getChanges();
    if (this._previewDebounceTimer) clearTimeout(this._previewDebounceTimer);
    
    this._previewDebounceTimer = setTimeout(async () => {
      try {
        const changes = tab.getChanges();
        const preview = await this._orgService.previewSubscriptionUpdate(this._orgId, changes.planId, changes.moduleIds);
        
        const delta = Number(preview.delta_cents) || 0;
        const recurringDelta = Number(changes.recurringDeltaCents) || 0;
        const intervalSuffix = changes.interval === 'year' ? '/yr' : '/mo';
        
        let msg = '';
        if (delta >= 50) {
          msg = `Immediate charge: $${(delta / 100).toFixed(2)} for the remainder of the cycle.`;
        } else if (delta > 0) {
          msg = `Charge of $${(delta / 100).toFixed(2)} will be added to your next invoice (below $0.50 threshold).`;
        } else if (delta < 0) {
          msg = `Credit of $${(Math.abs(delta) / 100).toFixed(2)} will be applied to your balance.`;
        } else if (recurringDelta > 0) {
          msg = `No immediate charge. Your subscription will increase by $${(recurringDelta / 100).toFixed(2)}${intervalSuffix} starting next cycle.`;
        } else if (recurringDelta < 0) {
          msg = `Subscription will decrease by $${(Math.abs(recurringDelta) / 100).toFixed(2)}${intervalSuffix} starting next cycle.`;
        } else {
          msg = `No immediate charge for these changes.`;
        }
        
        if (saveBar) saveBar.setAttribute('message', msg);
      } catch (e) {
        if (saveBar) saveBar.setAttribute('message', 'Error previewing changes');
      }
    }, 400);
  }
}

if (!customElements.get('app-organization')) {
  customElements.define('app-organization', AppOrganization);
}
